import React, { useCallback, useContext, useState } from "react";
import UploadImageLayout from "../../layouts/UploadImageLayout";
import { Text, Button } from "react_reusable_components";
import { Divider } from "antd";
import { getLocalizedMessage } from "../../UtilityFunctions/utils";
import { useIntl } from "react-intl";
import variables from "../../themes/base/styles/variables";
import { ThreeRow } from "../../core/layouts";
import { useDropzone } from "react-dropzone";
import FileUploadImageErrorModal from "../FileUploadImageErrorModal";
import useResponsive from "../../core/hooks/useResponsive";
import { GlobalLoaderContext } from "../../globalContext/globalLoader/globalLoaderProvider";
import MediaService from "../../services/media-service";

const UploadAddUserFormImageContainerStyle = {
  dragAndDropTextStyle: {
    fontWeight: variables.fontFamilyRegular,
    lineHeight: variables.fontSizeNormal,
    fontSize: variables.fontSizeSmall,
    color: variables.textDefault,
  },
  maximumSizeTextStyle: {
    fontWeight: variables.fontFamilyRegular,
    lineHeight: variables.fontSizeNormal,
    fontSize: variables.fontSizeSmallest,
    color: variables.grey2,
  },
  orTextStyle: {
    color: variables.grey6,
    fontWeight: variables.fontFamilyRegular,
    lineHeight: variables.fontSizeNormal,
    fontSize: variables.fontSizeNormal,
  },
  leftSubSectionStyle: {
    display: variables.dFlex,
    justifyContent: variables.justifyCenter,
    flexDirection: "column",
    gap: variables.gapXS1,
  },
  middleSectionStyle: {
    display: variables.dFlex,
    gap: variables.gapXS,
    alignItems: variables.alignCenter,
  },
  dividerStyle: {
    height: "1.469rem",
    color: variables.grey7,
  },
  browseFileTextStyle: {
    backgroundColor: variables.textDefault,
    padding: `${variables.paddingSmallest} ${variables.paddingSmall}`,
    fontSize: variables.fontSizeSmall,
    lineHeight: variables.lineHeightNormal,
    textAlign: "center",
    display: variables.dFlex,
    alignItems: variables.alignCenter,
  },
  uploadImageContainerStyle: {
    width: variables.fullWidth,
    cursor: "pointer",
  },
};

export default function UploadUserImageContainer({
  setSelectedImage,
  maxSize,
  acceptedFilesTypes,
  minSize,
  maxFiles,
  type,
  setImageUrl,
}) {
  const intl = useIntl();
  const loaderDispatch = useContext(GlobalLoaderContext)[1];
  const responsive = useResponsive();
  const [openModal, setOpenModal] = useState(false);
  const [isFileRejectedHeader, setIsFileRejectedHeader] = useState("");
  const [isFileRejectedDescription, setIsFileRejectedDescription] =
    useState("");

  const onDrop = useCallback(
    async (acceptedFiles, rejectedFiles) => {
      const currentFile = acceptedFiles?.[0];

      if (!acceptedFilesTypes.includes(currentFile?.name?.split(".").pop())) {
        setOpenModal(true);
        setIsFileRejectedHeader(
          getLocalizedMessage(intl, "uploadImage.error.formatNotSupported")
        );
        setIsFileRejectedDescription(
          getLocalizedMessage(
            intl,
            "uploadImage.error.formatSupportedPngAndJpeg"
          )
        );
        return;
      }

      if (currentFile?.size > maxSize) {
        setOpenModal(true);
        setIsFileRejectedHeader(
          getLocalizedMessage(intl, "uploadImage.error.sizeExceeded")
        );
        setIsFileRejectedDescription(
          getLocalizedMessage(intl, "uploadImage.error.maxSizeAllowed")
        );
        return;
      }
      const formData = new FormData();
      formData.append("file", acceptedFiles?.[0]);
      formData.append("type", type);

      await MediaService.uploadMedia({
        loaderDispatch,
        body: formData,
        queryParams: { type },
        intl,
      }).then((res) => {
        setImageUrl(res?.data?.data?.id);
        setSelectedImage(acceptedFiles);
      });
    },
    [acceptedFilesTypes, intl, maxSize, setImageUrl, setSelectedImage, type]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    minSize: minSize,
    maxFiles: maxFiles,
  });

  return (
    <div
      style={UploadAddUserFormImageContainerStyle.uploadImageContainerStyle}
      {...getRootProps()}
    >
      <input {...getInputProps()} accept="image/*" />
      <UploadImageLayout
        leftSection={<img alt="block" src={"/icons/uploadImage.svg"} />}
        leftSectionStyle={UploadAddUserFormImageContainerStyle.leftSectionStyle}
        leftSubSection={
          <div
            style={{
              ...UploadAddUserFormImageContainerStyle.leftSubSectionStyle,
              ...(!responsive.isMd && {
                alignItems: variables.alignCenter,
              }),
            }}
          >
            <Text
              style={UploadAddUserFormImageContainerStyle.dragAndDropTextStyle}
            >
              {getLocalizedMessage(intl, "uploadImage.text.dropFileHere")}
            </Text>
            <Text
              style={UploadAddUserFormImageContainerStyle.maximumSizeTextStyle}
            >
              {getLocalizedMessage(intl, "uploadImage.text.maxSizeFile")}
            </Text>
          </div>
        }
        middleSection={
          <ThreeRow
            style={UploadAddUserFormImageContainerStyle.middleSectionStyle}
            topSection={
              <Divider
                type="vertical"
                sstyle={UploadAddUserFormImageContainerStyle.dividerStyle}
              />
            }
            middleSection={
              <Text style={UploadAddUserFormImageContainerStyle.orTextStyle}>
                {getLocalizedMessage(intl, "uploadImage.text.or")}
              </Text>
            }
            bottomSection={
              <Divider
                type="vertical"
                style={UploadAddUserFormImageContainerStyle.dividerStyle}
              />
            }
          />
        }
        rightSection={
          <Button
            style={UploadAddUserFormImageContainerStyle.browseFileTextStyle}
          >
            {getLocalizedMessage(intl, "uploadImage.text.browseFile")}
          </Button>
        }
      />
      <FileUploadImageErrorModal
        open={openModal}
        header={isFileRejectedHeader}
        description={isFileRejectedDescription}
        onClose={() => setOpenModal(false)}
        buttonText={getLocalizedMessage(intl, "button.text.goBack")}
        iconSrc={"/icons/warningIcon.svg"}
      />
    </div>
  );
}
